import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader, EmptyState, StatusPill } from "@/components/common";
import { api, errMsg, fmtDate, fmtDateTime, downloadFile } from "@/lib/api";

const CRM = ["new", "contacted", "follow_up", "negotiation", "won", "lost"];

const CrmSheet = ({ row, onClose, onSaved }) => {
  const [status, setStatus] = useState("new");
  const [note, setNote] = useState("");
  const [acts, setActs] = useState([]);

  useEffect(() => {
    if (row) {
      setStatus(row.crm_status || "new");
      setNote("");
      api.get(`/agency/my-leads/${row.lead_id}/activities`).then((r) => setActs(r.data));
    }
  }, [row]);

  const save = async () => {
    try {
      await api.put(`/agency/my-leads/${row.lead_id}/crm`, { status, note });
      toast.success("CRM updated");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <Sheet open={!!row} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="crm-sheet">
        <SheetHeader><SheetTitle className="font-display text-left">{row?.lead.title}</SheetTitle></SheetHeader>
        <div className="mt-6 space-y-4">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="crm-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{CRM.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>)}</SelectContent>
          </Select>
          <Textarea placeholder="Add a note (call outcome, next step...)" value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid="crm-note-input" />
          <Button className="w-full" onClick={save} data-testid="crm-save-button">Save update</Button>
          <div>
            <p className="flex items-center gap-2 text-sm font-semibold mt-6 mb-3"><History className="h-4 w-4" />Activity</p>
            {acts.length === 0 ? <p className="text-sm text-muted-foreground">No activity yet.</p> : acts.map((a) => (
              <div key={a.id} className="border-l-2 border-primary/30 pl-3 pb-4">
                <div className="flex items-center gap-2"><StatusPill status={a.status} /><span className="text-xs text-muted-foreground">{fmtDateTime(a.created_at)}</span></div>
                {a.note && <p className="text-sm mt-1">{a.note}</p>}
              </div>
            ))}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default function MyLeads() {
  const [rows, setRows] = useState(null);
  const [active, setActive] = useState(null);
  const load = () => api.get("/agency/my-leads").then((r) => setRows(r.data));

  useEffect(() => {
    load();
  }, []);

  const exportCsv = async () => {
    try {
      await downloadFile("/agency/my-leads/export", "my-leads.csv");
      toast.success("Export downloaded");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <PageHeader eyebrow="CRM" title="My Leads" subtitle="Leads you've unlocked. Subscription-based access ends when your plan expires; credit unlocks are permanent."
        actions={<Button variant="outline" onClick={exportCsv} data-testid="export-csv-button"><Download className="h-4 w-4 mr-2" />Export CSV</Button>} />
      {rows && rows.length === 0 ? (
        <EmptyState title="No unlocked leads yet" text="Unlock a lead from the marketplace to start your pipeline." testId="my-leads-empty"
          action={<Button asChild><Link to="/app/marketplace">Browse marketplace</Link></Button>} />
      ) : (
        <div className="bg-card rounded-xl border overflow-x-auto fade-up">
          <Table data-testid="my-leads-table">
            <TableHeader><TableRow><TableHead>Lead</TableHead><TableHead>Contact</TableHead><TableHead>Access</TableHead><TableHead>Status</TableHead><TableHead>Unlocked</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {(rows || []).map((r) => (
                <TableRow key={r.lead_id} data-testid={`my-lead-row-${r.lead_id}`}>
                  <TableCell className="min-w-[220px]"><Link to={`/app/leads/${r.lead_id}`} className="font-medium hover:text-primary">{r.lead.title}</Link><p className="text-xs text-muted-foreground">{r.lead.category} · {r.lead.country}</p></TableCell>
                  <TableCell className="min-w-[200px] text-sm">
                    {r.contact ? (<><p className="font-medium">{r.contact.client_name}</p><p className="text-xs text-muted-foreground font-mono">{r.contact.phone}</p><p className="text-xs text-muted-foreground">{r.contact.email}</p></>)
                      : <span className="text-xs text-rose-600 dark:text-rose-400" data-testid="access-lost-label">Access ended — subscription expired</span>}
                  </TableCell>
                  <TableCell><span className="text-xs font-mono uppercase">{r.method}</span></TableCell>
                  <TableCell><StatusPill status={r.crm_status} testId={`crm-status-${r.lead_id}`} /></TableCell>
                  <TableCell className="text-sm whitespace-nowrap">{fmtDate(r.unlocked_at)}</TableCell>
                  <TableCell><Button size="sm" variant="ghost" onClick={() => setActive(r)} data-testid={`crm-open-${r.lead_id}`}>Update</Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <CrmSheet row={active} onClose={() => setActive(null)} onSaved={load} />
    </div>
  );
}
